import { Info, Warning } from '@phosphor-icons/react';
import { Button } from '@clay-run/glaze/button';
import { Icon } from '@clay-run/glaze/icon';
import { IconButton } from '@clay-run/glaze/iconbutton';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { TableCell } from '@clay-run/glaze/table';

import { SfdcCloudIcon } from '../../../_shared/components/flow';
import type { CampaignMember } from '../../../_shared/launcher-campaign.types';

/**
 * The per-person table cells shared by `MembersPanel` (Qualify / Sequences) and
 * `ReviewRungMembers` (Review folders) — plan 2026-10-15-003 U4. One
 * definition, so the two tables cannot drift on how a person is named, how
 * their email is shown or what the details control is called.
 */

export interface MemberDisplay {
  /** The member's email identity: the Contact email, else the Lead email (a Lead-sourced member has no Contact row). */
  email: string;
  /** What the row is called: name, else email, else id. */
  label: string;
  /**
   * The Email column text — empty when Name has ALREADY fallen back to the
   * email, so one row never shows the same address twice (the cell then
   * renders an em dash).
   */
  emailCell: string;
}

export function memberDisplay(member: CampaignMember): MemberDisplay {
  const email = (member.contact_email ?? '').trim() || (member.lead_email ?? '').trim();
  const label = member.contact_name || email || member.id;
  return { email, label, emailCell: email && email !== label ? email : '' };
}

interface MemberNameCellProps {
  member: CampaignMember;
  label: string;
  /** The Name click. Review passes "open this person's panel"; the Members panel passes "scope the copy preview". */
  onSelect: () => void;
  selected?: boolean;
  /** Shows the unresolved-merge-fields warning (Members panel only). */
  unresolved?: boolean;
}

export function MemberNameCell({ member, label, onSelect, selected = false, unresolved = false }: MemberNameCellProps) {
  return (
    <TableCell>
      <div className="flex min-w-0 items-center gap-1">
        {/*
          A real button, not a row `onClick`: the click has to be
          keyboard-reachable and needs an accessible name (the member's), which
          is also what the browser-QA accessibility snapshot reads. The name is
          pinned with `aria-label` so the warning icon's own label never folds
          into it.
        */}
        <button
          type="button"
          className="flex min-w-0 items-center gap-1 text-left"
          aria-label={label}
          aria-current={selected ? 'true' : undefined}
          onClick={onSelect}
        >
          {unresolved && (
            <Icon type={Warning} aria-label="Unresolved merge fields" className="text-content-warning" />
          )}
          <span className="truncate">{label}</span>
        </button>
        {/* The SFDC out-link: a SEPARATE affordance — it never selects the row or opens a panel. */}
        <SfdcCloudIcon kind="Contact" id={member.contact_id} />
        {!member.contact_id && <SfdcCloudIcon kind="Lead" id={member.lead_id} />}
      </div>
    </TableCell>
  );
}

export function MemberEmailCell({ emailCell }: { emailCell: string }) {
  return (
    <TableCell>
      {emailCell ? (
        // Truncated in the cell, full address in `title` — the column exists to
        // tell two same-named members apart, which a clipped address still does.
        <span className="block max-w-[16rem] truncate" title={emailCell}>
          {emailCell}
        </span>
      ) : (
        '—'
      )}
    </TableCell>
  );
}

export function MemberStatusCell({ member }: { member: CampaignMember }) {
  return <TableCell>{member.cm_status ? <StatusBadge status="neutral">{member.cm_status}</StatusBadge> : '—'}</TableCell>;
}

export function MemberCreatedCell({ member }: { member: CampaignMember }) {
  return <TableCell>{member.created_date ? new Date(member.created_date).toLocaleDateString() : '—'}</TableCell>;
}

/**
 * The per-row details control — a SEPARATE control from the Name button.
 *
 * `icon` (default, `MembersPanel`): the ghost (i) named "Details for <name>".
 * `preview` (Review folders only): a primary "Preview" button named "Preview
 * <name>". Both open the same person panel — only the look and name differ.
 */
export function MemberDetailsButton({
  label,
  onClick,
  variant = 'icon',
}: {
  label: string;
  onClick: () => void;
  variant?: 'icon' | 'preview';
}) {
  return (
    <TableCell>
      {variant === 'preview' ? (
        <Button variant="primary" size="xs" aria-label={`Preview ${label}`} onClick={onClick}>
          Preview
        </Button>
      ) : (
        <IconButton icon={Info} variant="ghost" size="xs" aria-label={`Details for ${label}`} onClick={onClick} />
      )}
    </TableCell>
  );
}
